import React from 'react';
import styled from '@emotion/styled';

import {getProductId, useProductCompare} from '$features/product-compare/product-compare.store';
import {ToolTip} from "$shared/components/hover-card-tooltip/tool-tip";

type Props = {
    product: any;
};
export const CompareProductButton = ({ product }: Props) => {
    const { products, addProduct, removeProduct, showTray, } = useProductCompare();

    const productId = getProductId(product);

    const isSelected = products.some(
        (item) => getProductId(item) === productId
    );

    const isDisabled = products.length >= 4 && !isSelected;

    const toggleCompare = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!productId) return;

        if (isSelected) {
            removeProduct(product);
            return;
        }

        if (products.length >= 4) {
            showTray();
            return;
        }

        addProduct(product);
    };

    return (
        <ToolTip
            content={
                isDisabled
                    ? 'Maximum 4 products can be compared'
                    : isSelected
                        ? 'Remove from comparison'
                        : 'Compare products'
            }
            position="top"
            arrow={true}
        >
            <span>
                <Button
                    type="button"
                    onClick={toggleCompare}
                    $selected={isSelected}
                    $limitReached={isDisabled}
                    aria-label={
                        isSelected
                            ? 'Remove product from comparison'
                            : 'Compare products'
                    }
                    aria-pressed={isSelected}
                >
                    <CompareIcon
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        $selected={isSelected}
                    >
                        <path d="M7 7h11l-3-3" />
                        <path d="M18 7l-3 3" />
                        <path d="M17 17H6l3 3" />
                        <path d="M6 17l3-3" />
                    </CompareIcon>

                   <Label $selected={isSelected}>Compare</Label>
                </Button>
            </span>
        </ToolTip>
    );
};

const Button = styled.button<{
    $selected: boolean;
    $limitReached: boolean;
}>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    width: 40px;
    height: 40px;
    padding: 0;

    border: 0;
    background: transparent;

    color: ${({ $selected, $limitReached }) => {
        if ($selected) return '#168bd2';
        if ($limitReached) return '#aaa';
        return '#222';
    }};

    cursor: pointer;

    &:hover {
        color: ${({ $selected, $limitReached }) => {
            if ($selected) return '#168bd2';
            if ($limitReached) return '#888';
            return '#555';
        }};
    }
`;

const CompareIcon = styled.svg<{ $selected: boolean }>`
    width: 25px;
    height: 25px;

    fill: none;
    stroke: ${({ $selected }) =>
    $selected ? '#168bd2' : 'currentColor'};
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
`;

const Label = styled.span<{ $selected: boolean }>`
    position: absolute;
    top: 34px;
    left: 50%;
    transform: translateX(-50%);

    font-size: 12px;
    line-height: 1.2;
    white-space: nowrap;

    color: ${({ $selected }) =>
            $selected ? '#168bd2' : 'inherit'};
`;

export default CompareProductButton;